import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import {
  User,
  Activity,
  Users,
  Layers,
  Settings,
  Clock,
  ArrowRight,
  ChevronDown,
  Shield,
  Smartphone,
  RefreshCw,
  Building2
} from 'lucide-react'
import {
  PremiumGlassCard,
  ScrollReveal,
  StaggeredReveal,
} from '@/components/ui'
import AuthModal from '../components/AuthModal'
import EncryptionField from '../components/EncryptionField'
import AidCardSection from '../components/AidCardSection'
import { useLocale } from '../hooks/useLocale'
import { translations } from '../lib/translations'
import { issueWalletPass, generateQRG, getDeviceId } from '../lib/wallet-client'

/**
 * HomePage - Sovereign Identity Landing
 *
 * Intro: "Identity Verification Ceremony" — museum-grade entrance
 *   Phase 1: The Gaze (luminous lens)
 *   Phase 2: Recognition ("Verified" crystallization)
 *   Phase 3: Reveal (staggered hero content)
 *   Phase 4: Settle (CTAs + scroll indicator)
 */

const INTRO_SESSION_KEY = 'lukhas_id_intro_shown'

const brandFontStyle: React.CSSProperties = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", "Helvetica", Arial, sans-serif',
  fontWeight: 100,
}

// Staggered character animation for the wordmark
const WORDMARK_CHARS = ['Λ', 'i', 'D']

export default function HomePage() {
  const [introPhase, setIntroPhase] = useState<'idle' | 'gaze' | 'recognition' | 'reveal' | 'settle' | 'done'>('idle')
  const [loaded, setLoaded] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const navigate = useNavigate()
  const prefersReducedMotion = useReducedMotion()
  const { t, locale } = useLocale()

  const shouldSkipIntro = useCallback(() => {
    if (prefersReducedMotion) return true
    try {
      return sessionStorage.getItem(INTRO_SESSION_KEY) === 'true'
    } catch {
      return false
    }
  }, [prefersReducedMotion])

  const markIntroShown = useCallback(() => {
    try {
      sessionStorage.setItem(INTRO_SESSION_KEY, 'true')
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    if (shouldSkipIntro()) {
      setIntroPhase('done')
      setLoaded(true)
      return
    }

    // "Identity Verification Ceremony" — 4.5s
    const timers: ReturnType<typeof setTimeout>[] = []

    // Phase 1: The Gaze — lens fades in (100ms)
    timers.push(setTimeout(() => setIntroPhase('gaze'), 100))

    // Phase 2: Recognition — lens contracts, "Verified" appears (1200ms)
    timers.push(setTimeout(() => setIntroPhase('recognition'), 1200))

    // Phase 3: Reveal — lens dissolves, hero content fades up (2500ms)
    timers.push(setTimeout(() => setIntroPhase('reveal'), 2500))

    // Phase 4: Settle — CTAs appear, intro overlay fades out (3800ms)
    timers.push(setTimeout(() => setIntroPhase('settle'), 3800))

    // Done — fully interactive (4500ms)
    timers.push(setTimeout(() => {
      setIntroPhase('done')
      setLoaded(true)
      markIntroShown()
    }, 4500))

    return () => timers.forEach(clearTimeout)
  }, [shouldSkipIntro, markIntroShown])

  const openLoginModal = () => setAuthModalOpen(true)
  const openRegister = () => navigate('/onboarding')

  const namespaceKeys = [
    { icon: User, key: 'core' as const },
    { icon: Activity, key: 'behavioral' as const },
    { icon: Users, key: 'relational' as const },
    { icon: Layers, key: 'contextual' as const },
    { icon: Settings, key: 'preference' as const },
    { icon: Clock, key: 'temporal' as const },
  ]

  const namespaces = useMemo(() => namespaceKeys.map(ns => {
    const nsData = translations.namespaces[ns.key]
    return {
      icon: ns.icon,
      name: (nsData.name as Record<string, string>)[locale],
      description: (nsData.description as Record<string, string>)[locale],
    }
  }), [locale])

  // Has intro progressed far enough to show hero content?
  const showHero = introPhase === 'reveal' || introPhase === 'settle' || introPhase === 'done'
  const showCTAs = introPhase === 'settle' || introPhase === 'done'

  return (
    <>
      {/* ─── Intro: Identity Verification Ceremony ─── */}
      <AnimatePresence>
        {introPhase !== 'done' && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
            style={{ background: 'rgba(5, 5, 18, 0.85)' }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            role="presentation"
            aria-hidden="true"
          >
            {/* Phase 1: The Gaze — luminous lens ring */}
            <AnimatePresence>
              {(introPhase === 'gaze' || introPhase === 'recognition') && (
                <motion.div className="absolute flex flex-col items-center">
                  {/* Lens ring */}
                  <motion.div
                    className="rounded-full"
                    style={{
                      border: '1px solid rgba(45, 212, 191, 0.5)',
                      boxShadow: '0 0 40px rgba(45, 212, 191, 0.15), inset 0 0 20px rgba(45, 212, 191, 0.05)',
                    }}
                    initial={{ width: 0, height: 0, opacity: 0 }}
                    animate={introPhase === 'gaze' ? {
                      width: 80, height: 80, opacity: 1,
                    } : {
                      width: 40, height: 40, opacity: 1,
                    }}
                    exit={{ opacity: 0, scale: 1.3 }}
                    transition={introPhase === 'gaze' ? {
                      duration: 0.8, ease: [0.16, 1, 0.3, 1],
                    } : {
                      type: 'spring', stiffness: 300, damping: 20,
                    }}
                  />

                  {/* Radial light from lens */}
                  <motion.div
                    className="absolute rounded-full"
                    style={{
                      width: 200, height: 200,
                      background: 'radial-gradient(circle, rgba(45, 212, 191, 0.08) 0%, transparent 70%)',
                    }}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                  />

                  {/* Phase 2: "Verified" text crystallizes below lens */}
                  <AnimatePresence>
                    {introPhase === 'recognition' && (
                      <motion.span
                        className="mt-6 text-white/60"
                        style={{
                          fontSize: 11,
                          fontWeight: 200,
                          letterSpacing: '0.35em',
                          textTransform: 'uppercase' as const,
                        }}
                        initial={{ opacity: 0, filter: 'blur(4px)', y: 5 }}
                        animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
                        exit={{ opacity: 0, scale: 1.05 }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                      >
                        Verified
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Hero Section ─── */}
      <section className="min-h-screen flex flex-col justify-center relative pt-16 overflow-hidden" aria-labelledby="hero-heading">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-24 w-full">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center justify-center">
            {/* Typography */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={showHero ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="flex-1 max-w-xl text-center lg:text-left"
            >
              {/* Wordmark with staggered character animation */}
              <div className="mb-6">
                <div className="flex items-baseline justify-center lg:justify-start gap-0.5">
                  {WORDMARK_CHARS.map((char, i) => (
                    <motion.span
                      key={char}
                      className="text-6xl md:text-8xl tracking-[0.15em] text-white"
                      style={{
                        ...brandFontStyle,
                        textShadow: char === 'Λ' ? '0 0 20px rgba(45, 212, 191, 0.4)' : undefined,
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={showHero ? { opacity: char === 'D' ? 0.6 : 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ duration: 0.6, delay: 0.1 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                    >
                      {char}
                    </motion.span>
                  ))}
                </div>
              </div>

              <h1 id="hero-heading" className="mb-8">
                <span className="block text-[clamp(2.5rem,8vw,5.5rem)] font-extralight tracking-[-0.03em] leading-[1.05] text-white">
                  {t('hero', 'title1')}
                </span>
                <span className="block text-[clamp(2.5rem,8vw,5.5rem)] font-extralight tracking-[-0.03em] leading-[1.05] text-white/45 mt-2">
                  {t('hero', 'title2')}
                </span>
              </h1>

              <motion.p
                className="text-xl md:text-2xl font-normal text-white/75 max-w-md mb-10 leading-[1.6]"
                initial={{ opacity: 0 }}
                animate={showHero ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {t('hero', 'subtitle')}
              </motion.p>

              {/* CTAs — fade in at settle phase */}
              <motion.div
                className="flex flex-col sm:flex-row gap-5 justify-center lg:justify-start items-center"
                role="group"
                aria-label={t('a11y', 'accountActions')}
                initial={{ opacity: 0, y: 15 }}
                animate={showCTAs ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                <motion.button
                  onClick={openRegister}
                  className="group relative px-8 py-3.5 rounded-full border border-white/20 bg-white/[0.03] backdrop-blur-sm text-white text-sm font-light tracking-[0.15em] uppercase transition-all duration-300 hover:border-white/40 hover:bg-white/[0.06]"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(255, 255, 255, 0.06), inset 0 0 30px rgba(255, 255, 255, 0.03)'
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 0 0 0 rgba(255, 255, 255, 0)'
                  }}
                >
                  <span className="flex items-center gap-3">
                    <span>{t('hero', 'ctaCreate')}</span>
                    <ArrowRight className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-300" aria-hidden="true" />
                  </span>
                </motion.button>

                <motion.button
                  onClick={openLoginModal}
                  className="group text-white/40 text-sm font-light tracking-[0.12em] uppercase hover:text-white/70 transition-colors duration-300 flex items-center gap-2"
                  whileHover={{ x: 2 }}
                >
                  {t('header', 'signIn')}
                  <span className="w-4 h-px bg-white/20 group-hover:bg-white/40 group-hover:w-6 transition-all duration-300" />
                </motion.button>
              </motion.div>

              {/* Trust indicators */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={showCTAs ? { opacity: 1 } : { opacity: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="flex flex-wrap items-center justify-center lg:justify-start gap-4 lg:gap-6 mt-12 pt-8 border-t border-white/5"
              >
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/80" />
                  <span className="text-[11px] text-white/55 font-light tracking-wide">Zero-Knowledge</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-400/80" />
                  <span className="text-[11px] text-white/55 font-light tracking-wide">Wallet Pass</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400/80" />
                  <span className="text-[11px] text-white/55 font-light tracking-wide">NFC Ready</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/80" />
                  <span className="text-[11px] text-white/55 font-light tracking-wide">Selective Sharing</span>
                </div>
              </motion.div>
            </motion.div>

            {/* iPhone 15 Pro with Wallet Pass */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={showHero ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }}
              transition={{ duration: 1.5, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative flex flex-col items-center justify-center flex-shrink-0"
            >
              {/* Multi-layer ambient glow */}
              <div className="absolute w-[500px] h-[700px] rounded-full blur-[150px] opacity-20 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at center, #14b8a6 0%, #0d9488 30%, #0f766e 60%, transparent 100%)' }}
              />
              <div className="absolute w-[400px] h-[600px] rounded-full blur-[100px] opacity-10 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at center, #a855f7 0%, #6366f1 50%, transparent 100%)' }}
              />

              {/* 3D iPhone Container */}
              <div style={{ perspective: '1200px' }}>
                <motion.div
                  className="relative"
                  animate={prefersReducedMotion ? {} : {
                    rotateY: [-4, 4],
                    rotateX: [2, -2],
                  }}
                  transition={{
                    rotateY: { duration: 10, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' },
                    rotateX: { duration: 8, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' },
                  }}
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  {/* iPhone 15 Pro Frame */}
                  <div
                    className="relative w-[280px] md:w-[320px] h-[560px] md:h-[640px] rounded-[50px] md:rounded-[58px] overflow-hidden"
                    style={{
                      background: 'linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 50%, #0a0a0a 100%)',
                      boxShadow: `
                        0 60px 120px -20px rgba(0, 0, 0, 0.8),
                        0 40px 80px -20px rgba(20, 184, 166, 0.2),
                        inset 0 0 0 2px rgba(60, 60, 60, 0.5),
                        inset 0 1px 0 0 rgba(255, 255, 255, 0.1)
                      `,
                    }}
                  >
                    {/* Titanium frame edge highlight */}
                    <div className="absolute inset-0 rounded-[50px] md:rounded-[58px] pointer-events-none"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 30%, transparent 70%, rgba(255,255,255,0.05) 100%)',
                      }}
                    />

                    {/* Screen bezel */}
                    <div className="absolute inset-[3px] md:inset-[4px] rounded-[47px] md:rounded-[54px] bg-black overflow-hidden">
                      {/* Dynamic Island */}
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[100px] md:w-[120px] h-[32px] md:h-[36px] bg-black rounded-full z-20 flex items-center justify-center">
                        <div className="w-[10px] h-[10px] rounded-full bg-zinc-900 mr-12" />
                      </div>

                      {/* Status Bar */}
                      <div className="absolute top-0 left-0 right-0 h-12 px-6 flex items-center justify-between z-10">
                        <span className="text-white text-[13px] font-semibold">9:41</span>
                        <div className="flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M2 16.5l3-3 3 3 4-4 4 4 3-3 3 3V18H2v-1.5z"/>
                          </svg>
                          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
                          </svg>
                          <div className="flex items-center gap-0.5">
                            <div className="w-6 h-3 rounded-sm border border-white/80 flex items-center p-0.5">
                              <div className="w-4 h-full bg-emerald-400 rounded-sm"/>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Apple Wallet UI */}
                      <div className="absolute inset-0 pt-14 pb-6 px-3 md:px-4 flex flex-col"
                        style={{ background: 'linear-gradient(180deg, #1c1c1e 0%, #000000 100%)' }}
                      >
                        {/* Wallet Header */}
                        <div className="flex items-center justify-between mb-3 px-2">
                          <span className="text-white/60 text-sm font-medium">Done</span>
                          <span className="text-white text-lg font-semibold">Wallet</span>
                          <span className="text-white/0 text-sm">Done</span>
                        </div>

                        {/* Pass Card */}
                        <div className="flex-1 flex items-start justify-center pt-2">
                          <div
                            className="relative w-full max-w-[260px] md:max-w-[280px] rounded-[18px] overflow-hidden"
                            style={{
                              aspectRatio: '0.63',
                              background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 40%, #0f0f23 100%)',
                              boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.5)',
                            }}
                          >
                            {/* Holographic overlay */}
                            <motion.div
                              className="absolute inset-0 pointer-events-none opacity-40"
                              animate={prefersReducedMotion ? {} : {
                                background: [
                                  'linear-gradient(135deg, transparent 0%, rgba(20,184,166,0.3) 25%, rgba(168,85,247,0.3) 50%, rgba(59,130,246,0.3) 75%, transparent 100%)',
                                  'linear-gradient(225deg, transparent 0%, rgba(168,85,247,0.3) 25%, rgba(59,130,246,0.3) 50%, rgba(20,184,166,0.3) 75%, transparent 100%)',
                                ],
                              }}
                              transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                              style={{ mixBlendMode: 'overlay' }}
                            />

                            {/* Header Strip */}
                            <div className="relative h-16 md:h-[70px] overflow-hidden">
                              <div className="absolute inset-0"
                                style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 30%, #0891b2 60%, #0e7490 100%)' }}
                              />
                              <motion.div
                                className="absolute inset-0"
                                style={{
                                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
                                  backgroundSize: '200% 100%',
                                }}
                                animate={prefersReducedMotion ? {} : { backgroundPosition: ['200% 0', '-200% 0'] }}
                                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                              />
                              <div className="absolute top-3 left-4 flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                                  <span className="text-base font-extralight text-white">Λ</span>
                                </div>
                                <div>
                                  <div className="text-[8px] text-white/80 uppercase tracking-[0.15em]">LUKHAS</div>
                                  <div className="text-[11px] font-semibold text-white">Identity Pass</div>
                                </div>
                              </div>
                              <motion.div
                                className="absolute top-3 right-4"
                                animate={prefersReducedMotion ? {} : { opacity: [0.5, 1, 0.5] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              >
                                <svg className="w-5 h-5 text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M6 8.5a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v7a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4v-7Z"/>
                                  <path d="M15 8.5v7c0 .83-.67 1.5-1.5 1.5h-3c-.83 0-1.5-.67-1.5-1.5v-7c0-.83.67-1.5 1.5-1.5h3c.83 0 1.5.67 1.5 1.5Z"/>
                                </svg>
                              </motion.div>
                            </div>

                            {/* Pass Content */}
                            <div className="p-4 flex flex-col h-[calc(100%-64px)] md:h-[calc(100%-70px)]">
                              <div className="mb-2">
                                <div className="text-[8px] text-white/40 uppercase tracking-widest mb-0.5">Identity</div>
                                <div className="text-xl md:text-2xl font-light text-white tracking-wide" style={brandFontStyle}>
                                  ΛiD-••••-YOU
                                </div>
                              </div>

                              <div className="flex gap-4 mb-3">
                                <div>
                                  <div className="text-[7px] text-white/40 uppercase tracking-widest">Status</div>
                                  <div className="flex items-center gap-1">
                                    <motion.div
                                      className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                                      animate={prefersReducedMotion ? {} : { opacity: [1, 0.5, 1] }}
                                      transition={{ duration: 2, repeat: Infinity }}
                                    />
                                    <span className="text-[10px] text-emerald-400 font-medium">Verified</span>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[7px] text-white/40 uppercase tracking-widest">Tier</div>
                                  <div className="text-[10px] text-white font-medium">Personal</div>
                                </div>
                              </div>

                              <div className="flex gap-1 mb-2">
                                {['Passkey', 'NFC', 'ZKP'].map((cap) => (
                                  <div key={cap} className="px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-[7px] text-white/50">
                                    {cap}
                                  </div>
                                ))}
                              </div>

                              <div className="flex-1" />

                              <div className="flex items-end justify-between">
                                <div className="bg-white p-1 rounded-md">
                                  <svg className="w-10 h-10 md:w-12 md:h-12" viewBox="0 0 48 48">
                                    <rect fill="#1a1a2e" width="48" height="48" rx="2"/>
                                    <rect fill="#fff" x="4" y="4" width="12" height="12"/>
                                    <rect fill="#1a1a2e" x="6" y="6" width="8" height="8"/>
                                    <rect fill="#fff" x="8" y="8" width="4" height="4"/>
                                    <rect fill="#fff" x="32" y="4" width="12" height="12"/>
                                    <rect fill="#1a1a2e" x="34" y="6" width="8" height="8"/>
                                    <rect fill="#fff" x="36" y="8" width="4" height="4"/>
                                    <rect fill="#fff" x="4" y="32" width="12" height="12"/>
                                    <rect fill="#1a1a2e" x="6" y="34" width="8" height="8"/>
                                    <rect fill="#fff" x="8" y="36" width="4" height="4"/>
                                    <rect fill="#fff" x="20" y="4" width="4" height="4"/>
                                    <rect fill="#fff" x="20" y="12" width="4" height="4"/>
                                    <rect fill="#fff" x="4" y="20" width="4" height="4"/>
                                  </svg>
                                </div>
                                <div className="text-right">
                                  <div className="text-[8px] text-white/30 uppercase tracking-widest">lukhas.id</div>
                                  <div className="text-[10px] text-white/50">@you</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Bottom info */}
                        <div className="text-center mt-3">
                          <p className="text-[10px] text-white/30">Hold Near Reader</p>
                        </div>
                      </div>

                      {/* Home indicator */}
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 md:w-36 h-1 bg-white/30 rounded-full" />
                    </div>

                    {/* Side buttons */}
                    <div className="absolute left-0 top-32 w-[3px] h-8 bg-zinc-700 rounded-l-full" />
                    <div className="absolute left-0 top-44 w-[3px] h-14 bg-zinc-700 rounded-l-full" />
                    <div className="absolute left-0 top-60 w-[3px] h-14 bg-zinc-700 rounded-l-full" />
                    <div className="absolute right-0 top-44 w-[3px] h-20 bg-zinc-700 rounded-r-full" />
                  </div>

                  {/* Reflection */}
                  <div
                    className="absolute -bottom-28 left-1/2 w-[260px] h-28 rounded-[50px] pointer-events-none hidden md:block"
                    style={{
                      transform: 'translateX(-50%) scaleY(-1) perspective(500px) rotateX(50deg)',
                      background: 'linear-gradient(to bottom, rgba(26, 26, 26, 0.15) 0%, transparent 100%)',
                      filter: 'blur(8px)',
                    }}
                  />
                </motion.div>
              </div>

              {/* Apple Wallet Badge */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={showCTAs ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="mt-10 flex flex-col items-center gap-4"
              >
                <div className="flex items-center gap-3 px-5 py-2.5 bg-black rounded-xl border border-white/10 hover:border-white/20 transition-colors cursor-pointer">
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="5" width="16" height="14" rx="2.5" stroke="white" strokeWidth="1.2"/>
                    <rect x="4" y="8" width="16" height="3" fill="#34d399"/>
                    <rect x="4" y="11" width="16" height="3" fill="#2dd4bf"/>
                    <rect x="4" y="14" width="16" height="3" fill="#0891b2"/>
                  </svg>
                  <div className="flex flex-col">
                    <span className="text-[9px] text-white/40 uppercase tracking-widest leading-none">Add to</span>
                    <span className="text-[14px] text-white font-medium leading-tight tracking-tight">Apple Wallet</span>
                  </div>
                </div>

                <span className="text-[11px] text-white/30 tracking-widest uppercase">
                  Q2 2026
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={showCTAs ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          aria-hidden="true"
        >
          <motion.div
            animate={prefersReducedMotion ? {} : { y: [0, 6, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            className="flex flex-col items-center gap-2"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/20">Scroll</span>
            <ChevronDown className="w-4 h-4 text-white/20" />
          </motion.div>
        </motion.div>
      </section>

      {/* What is ΛiD */}
      <ScrollReveal direction="up" blur>
        <section className="py-32 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <span className="text-xs text-white/50 uppercase tracking-widest block mb-4">
                  {t('whatIsAiD', 'sectionLabel')}
                </span>
                <h2 className="text-4xl md:text-5xl font-extralight tracking-tight text-white mb-6">
                  {t('whatIsAiD', 'title1')}
                  <br />
                  <span className="text-white/40">{t('whatIsAiD', 'title2')}</span>
                </h2>
                <p className="text-lg text-white/70 font-light leading-relaxed max-w-md">
                  {t('whatIsAiD', 'description')}
                </p>
              </div>

              <div className="space-y-6">
                {[
                  { key: 'zeroKnowledge', labelKey: 'label', valueKey: 'value' },
                  { key: 'postQuantum', labelKey: 'label', valueKey: 'value' },
                  { key: 'singleSignOn', labelKey: 'label', valueKey: 'value' },
                ].map((item, i) => {
                  const featureData = translations.whatIsAiD.features[item.key as keyof typeof translations.whatIsAiD.features]
                  const label = (featureData.label as Record<string, string>)[locale]
                  const value = (featureData.value as Record<string, string>)[locale]
                  return (
                    <motion.div
                      key={item.key}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: '-80px' }}
                      transition={{ duration: 0.6, delay: i * 0.1 }}
                      className="flex items-center justify-between py-4 border-b border-white/5"
                    >
                      <span className="text-white/80 font-light">{label}</span>
                      <span className="text-white/55 text-sm font-light">{value}</span>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Six Namespaces */}
      <ScrollReveal direction="up" blur>
        <section className="py-32 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-xs text-white/50 uppercase tracking-widest block mb-4">
                {t('namespaces', 'sectionLabel')}
              </span>
              <h2 className="text-4xl md:text-5xl font-extralight tracking-tight text-white mb-6">
                {t('namespaces', 'title1')}
                <br />
                <span className="text-white/40">{t('namespaces', 'title2')}</span>
              </h2>
              <p className="text-lg text-white/70 font-light max-w-lg mx-auto leading-relaxed">
                {t('namespaces', 'description')}
              </p>
            </div>

            <StaggeredReveal staggerDelay={0.08} direction="up" className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {namespaces.map((ns) => (
                <PremiumGlassCard
                  key={ns.name}
                  accent="identity"
                  dark
                  size="compact"
                  hover
                  accentGlow
                  style={{
                    backdropFilter: 'blur(40px) saturate(200%)',
                    WebkitBackdropFilter: 'blur(40px) saturate(200%)',
                    background: 'rgba(255, 255, 255, 0.05)',
                  }}
                >
                  <div className="p-6">
                    <ns.icon className="w-5 h-5 text-white/50 mb-4" />
                    <h3 className="text-lg font-light text-white mb-2 tracking-wide">
                      {ns.name}
                    </h3>
                    <p className="text-sm text-white/65 font-light leading-relaxed">
                      {ns.description}
                    </p>
                  </div>
                </PremiumGlassCard>
              ))}
            </StaggeredReveal>
          </div>
        </section>
      </ScrollReveal>

      {/* Security */}
      <ScrollReveal direction="up" blur>
        <section className="py-32 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-xs text-white/50 uppercase tracking-widest block mb-4">
                  {t('security', 'sectionLabel')}
                </span>
                <h2 className="text-4xl md:text-5xl font-extralight tracking-tight text-white mb-6">
                  {t('security', 'title1')}
                  <br />
                  <span className="text-white/40">{t('security', 'title2')}</span>
                </h2>
                <p className="text-lg text-white/70 font-light leading-relaxed max-w-md mb-8">
                  {t('security', 'description')}
                </p>

                <div className="space-y-4">
                  {(['webauthn', 'argon2', 'aes', 'zeroKnowledge'] as const).map((key, i) => {
                    const feature = (translations.security.features[key] as Record<string, string>)[locale]
                    return (
                      <motion.div
                        key={key}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}
                        className="flex items-center gap-3"
                      >
                        <div className="w-1 h-1 bg-white/40" aria-hidden="true" />
                        <span className="text-sm text-white/70 font-light">{feature}</span>
                      </motion.div>
                    )
                  })}
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 1.5, delay: 0.3 }}
                className="relative aspect-square flex items-center justify-center"
              >
                <EncryptionField />
              </motion.div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* ΛiD Card Section */}
      <AidCardSection
        designPreview={true}
        onAddToWallet={async () => {
          const deviceId = getDeviceId()
          const result = await issueWalletPass({
            platform: 'apple',
            deviceId,
            action: 'wallet.bind',
          })
          if (!result.ok) {
            throw new Error(result.error || 'Failed to issue pass')
          }
        }}
        onRequestQRG={async () => {
          return await generateQRG()
        }}
      />

      {/* Wallet Features */}
      <ScrollReveal direction="up" blur>
        <section className="py-32 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-xs text-white/50 uppercase tracking-widest block mb-4">
                {t('walletFeatures', 'sectionLabel')}
              </span>
              <h2 className="text-4xl md:text-5xl font-extralight tracking-tight text-white mb-6">
                {t('walletFeatures', 'title1')}
                <br />
                <span className="text-white/40">{t('walletFeatures', 'title2')}</span>
              </h2>
              <p className="text-lg text-white/70 font-light max-w-lg mx-auto leading-relaxed">
                {t('walletFeatures', 'description')}
              </p>
            </div>

            <StaggeredReveal staggerDelay={0.1} direction="up" className="grid md:grid-cols-2 gap-6 mb-16">
              {[
                { icon: Shield, key: 'privacy' as const, accent: 'emerald' as const },
                { icon: Smartphone, key: 'portable' as const, accent: 'cyan' as const },
                { icon: RefreshCw, key: 'revocable' as const, accent: 'blue' as const },
                { icon: Building2, key: 'enterprise' as const, accent: 'indigo' as const },
              ].map((feature) => {
                const featureData = translations.walletFeatures[feature.key]
                const label = (featureData.label as Record<string, string>)[locale]
                const value = (featureData.value as Record<string, string>)[locale]
                return (
                  <PremiumGlassCard
                    key={feature.key}
                    accent={feature.accent}
                    dark
                    hover
                    accentGlow
                    style={{
                      backdropFilter: 'blur(40px) saturate(200%)',
                      WebkitBackdropFilter: 'blur(40px) saturate(200%)',
                      background: 'rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <div className="p-8">
                      <feature.icon className="w-8 h-8 text-white/50 mb-4" />
                      <h3 className="text-xl font-light text-white mb-2">{label}</h3>
                      <p className="text-sm text-white/65 font-light leading-relaxed">{value}</p>
                    </div>
                  </PremiumGlassCard>
                )
              })}
            </StaggeredReveal>

            {/* Stats Row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-8 md:gap-16 py-8 border-t border-b border-white/5"
            >
              {[
                { labelKey: 'nfc' as const, valueKey: 'nfcValue' as const },
                { labelKey: 'refresh' as const, valueKey: 'refreshValue' as const },
                { labelKey: 'platforms' as const, valueKey: 'platformsValue' as const },
              ].map((stat) => {
                const statsData = translations.walletFeatures.stats
                const label = (statsData[stat.labelKey] as Record<string, string>)[locale]
                const value = (statsData[stat.valueKey] as Record<string, string>)[locale]
                return (
                  <div key={stat.labelKey} className="text-center">
                    <div className="text-2xl md:text-3xl font-extralight text-white mb-1">{value}</div>
                    <div className="text-xs text-white/40 uppercase tracking-widest">{label}</div>
                  </div>
                )
              })}
            </motion.div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal direction="up" blur>
        <section className="py-32 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-4xl md:text-6xl font-extralight tracking-tight text-white mb-6">
                {t('cta', 'title')}
              </h2>
              <p className="text-lg text-white/60 font-light mb-12 max-w-md mx-auto leading-relaxed">
                {t('cta', 'subtitle')}
              </p>

              <div className="flex justify-center">
                <motion.button
                  onClick={openRegister}
                  className="group relative px-8 py-3.5 rounded-full border border-white/20 bg-white/[0.03] backdrop-blur-sm text-white text-sm font-light tracking-[0.15em] uppercase transition-all duration-300 hover:border-white/40 hover:bg-white/[0.06]"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="flex items-center gap-3">
                    <span>{t('hero', 'ctaCreate')}</span>
                    <ArrowRight className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-300" aria-hidden="true" />
                  </span>
                </motion.button>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
    </>
  )
}
